import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import { getUser, requiresAutoLogout, clearUser } from '../../utils/auth';
import { updateUserLoginStatus, addLog } from '../../utils/storage';
import { getDeviceInfo, getIPAddress } from '../../utils/device';
import { getActiveEventId, setActiveEventId, DEFAULT_EVENT_ID } from '../../utils/event';

interface DashboardLayoutProps {
  children: React.ReactNode;
}

const INACTIVITY_TIMEOUT = 5 * 60 * 1000; // 5 minutes in milliseconds
const WARNING_TIME = 10 * 1000; // 10 seconds warning

export default function DashboardLayout({ children }: DashboardLayoutProps) {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const user = getUser();
  // Stable snapshot of id/username + whether auto-logout applies, so the
  // countdown effect below doesn't re-run (and reset the timer) on every render.
  const userSession = useMemo(
    () => ({
      id: user?.id ?? null,
      username: user?.username ?? null,
      needsAutoLogout: !!user && requiresAutoLogout(user),
    }),
    [user?.id, user?.username, user?.role]
  );
  const [showLogoutWarning, setShowLogoutWarning] = useState(false);
  const [countdown, setCountdown] = useState(10);
  const [remainingMs, setRemainingMs] = useState(INACTIVITY_TIMEOUT);
  const lastActivityRef = useRef<number>(Date.now());
  const tickIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Guard: non-master users can only operate inside an assigned event.
  useEffect(() => {
    if (user && user.role !== 'master_admin') {
      const allowed = (user.eventIds || []).length ? user.eventIds : [DEFAULT_EVENT_ID];
      if (!allowed.includes(getActiveEventId())) {
        setActiveEventId(allowed[0]);
      }
    }
  }, [user]);

  const handleAutoLogout = useCallback(async () => {
    if (!userSession.id) return;

    const { browser, device } = getDeviceInfo();
    const ipAddress = await getIPAddress();

    addLog({
      id: Date.now().toString(),
      userId: userSession.id,
      username: userSession.username || '',
      action: 'Auto Logout',
      details: 'Logged out due to 5 minutes of inactivity',
      ipAddress,
      deviceName: device,
      browser,
      timestamp: new Date().toISOString(),
    });

    updateUserLoginStatus(userSession.id, false);
    clearUser();
    navigate('/login');
  }, [userSession.id, userSession.username, navigate]);

  const handleStayLoggedIn = useCallback(() => {
    setShowLogoutWarning(false);
    setCountdown(10);
    lastActivityRef.current = Date.now();
    setRemainingMs(INACTIVITY_TIMEOUT);
  }, []);

  const handleLogoutNow = useCallback(() => {
    setShowLogoutWarning(false);
    handleAutoLogout();
  }, [handleAutoLogout]);

  useEffect(() => {
    if (!userSession.needsAutoLogout) return;

    let timeoutId: NodeJS.Timeout;
    let warningTimeoutId: NodeJS.Timeout;
    let countdownIntervalId: NodeJS.Timeout;

    const resetTimer = () => {
      clearTimeout(timeoutId);
      clearTimeout(warningTimeoutId);
      clearInterval(countdownIntervalId);
      setShowLogoutWarning(false);
      setCountdown(10);
      lastActivityRef.current = Date.now();
      setRemainingMs(INACTIVITY_TIMEOUT);

      warningTimeoutId = setTimeout(() => {
        setShowLogoutWarning(true);
        setCountdown(10);

        let secondsLeft = 10;
        countdownIntervalId = setInterval(() => {
          secondsLeft -= 1;
          setCountdown(secondsLeft);

          if (secondsLeft <= 0) {
            clearInterval(countdownIntervalId);
            handleAutoLogout();
          }
        }, 1000);
      }, INACTIVITY_TIMEOUT - WARNING_TIME);

      timeoutId = setTimeout(() => {
        handleAutoLogout();
      }, INACTIVITY_TIMEOUT);
    };

    // Only click-based events reset the timer (mousemove excluded)
    const events = ['mousedown', 'keypress', 'scroll', 'touchstart', 'click'];

    events.forEach(event => {
      document.addEventListener(event, resetTimer);
    });

    resetTimer();

    // Tick every second to update remaining time display
    tickIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - lastActivityRef.current;
      const remaining = Math.max(0, INACTIVITY_TIMEOUT - elapsed);
      setRemainingMs(remaining);
    }, 1000);

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(warningTimeoutId);
      clearInterval(countdownIntervalId);
      if (tickIntervalRef.current) clearInterval(tickIntervalRef.current);
      events.forEach(event => {
        document.removeEventListener(event, resetTimer);
      });
    };
  }, [userSession.needsAutoLogout, handleAutoLogout]);

  const formatRemaining = (ms: number) => {
    const totalSeconds = Math.ceil(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    if (minutes > 0) {
      return `${minutes}m ${seconds.toString().padStart(2, '0')}s`;
    }
    return `${seconds}s`;
  };

  const isNearExpiry = remainingMs <= 60 * 1000; // last 1 minute

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <Sidebar collapsed={collapsed} onToggle={() => setCollapsed(!collapsed)} />
      <main
        className={`transition-[margin-left] duration-300 pt-6 pb-8 px-4 sm:px-6 lg:px-8 ${
          collapsed ? 'ml-[68px]' : 'ml-60'
        }`}
      >
        {children}
      </main>

      {/* Session Countdown — bottom left */}
      {userSession.needsAutoLogout && !showLogoutWarning && (
        <div
          className={`fixed bottom-4 left-4 z-40 flex items-center gap-2 px-3 py-2 rounded-lg shadow-md text-xs font-medium transition-all duration-300 ${
            isNearExpiry
              ? 'bg-red-50 border border-red-200 text-red-600'
              : 'bg-white border border-gray-200 text-gray-500'
          }`}
        >
          <div className={`w-5 h-5 flex items-center justify-center`}>
            <i className={`ri-timer-line text-sm ${isNearExpiry ? 'text-red-500' : 'text-gray-400'}`}></i>
          </div>
          <span>
            Session expires in{' '}
            <span className={`font-bold ${isNearExpiry ? 'text-red-600' : 'text-gray-700'}`}>
              {formatRemaining(remainingMs)}
            </span>
          </span>
        </div>
      )}

      {/* Auto Logout Warning Modal */}
      {showLogoutWarning && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-8 text-center">
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-time-line text-4xl text-amber-600"></i>
            </div>

            <h3 className="text-2xl font-bold text-gray-900 mb-3">Inactivity Warning</h3>
            <p className="text-gray-600 mb-6">
              You will be automatically logged out in
            </p>

            <div className="mb-8">
              <div className="text-6xl font-bold text-amber-600 mb-2">
                {countdown}
              </div>
              <p className="text-sm text-gray-500">seconds</p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleLogoutNow}
                className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                Logout Now
              </button>
              <button
                onClick={handleStayLoggedIn}
                className="flex-1 px-6 py-3 bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Stay Logged In
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
